// index.js
'use strict';

import App from './component/App.jsx';

import {
  ManageProfileType,
  ProfileListPublishStatus,
  ProfileListType,
} from './resource/profileConstants.js';
import { LIVESTREAM_PATH } from './resource/liveStreamConstants.js';
import { CHAT_ID as AUTO_CHAT_CHAT_ID } from './resource/autoChatConstants.js';

import UserIdLayout, {
  loader as UserIdLayoutLoader,
  shouldRevalidate as UserIdLayoutShouldRevalidate,
} from './route/UserIdLayout.jsx';

import UsernameLayout, {
  loader as UsernameLayoutLoader,
  shouldRevalidate as UsernameLayoutShouldRevalidate,
} from './route/UsernameLayout.jsx';

// Routes with server side fetch - must use direct import (not lazy async)
import PostMessageIdLayout, {
  loader as PostMessageIdLayoutLoader,
} from './route/PostMessageIdLayout.jsx';

import ShortsMessageId, {
  loader as ShortsMessageIdLoader,
} from './route/ShortsMessageId.jsx';

import FlixMessageId, {
  loader as FlixMessageIdLoader,
} from './route/FlixMessageId.jsx';

import CategoryIdLayout from './route/CategoryIdLayout.jsx';

import CategoryIdListType, {
  loader as CategoryIdListTypeLoader,
} from './route/CategoryIdListType.jsx';

import HashtagHashtagType, {
  loader as HashtagHashtagTypeLoader,
} from './route/HashtagHashtagType.jsx';

import ProductId, { loader as ProductIdLoader } from './route/ProductId.jsx';

import SettingsFaq, {
  loader as SettingsFaqLoader,
} from './route/SettingsFaq.jsx';

import SettingsPreferences, {
  loader as SettingsPreferencesLoader,
} from './route/SettingsPreferences.jsx';

import Home, { loader as HomeLoader } from './route/Home.jsx';

import Feed, { loader as FeedLoader } from './route/Feed.jsx';

import Shorts, { loader as ShortsLoader } from './route/Shorts.jsx';

import CategoryId, { loader as CategoryIdLoader } from './route/CategoryId.jsx';

import UserCategoriesCategory, {
  loader as UserCategoriesCategoryLoader,
} from './route/UserCategoriesCategory.jsx';

import HashtagHashtag, {
  loader as HashtagHashtagLoader,
} from './route/HashtagHashtag.jsx';

import UserFeedGridCategory, {
  loader as UserFeedGridCategoryLoader,
} from './route/UserFeedGridCategory.jsx';

import UserFollowingGridCategory, {
  loader as UserFollowingGridCategoryLoader,
} from './route/UserFollowingGridCategory.jsx';

import UserLeaderboardGridCategory, {
  loader as UserLeaderboardGridCategoryLoader,
} from './route/UserLeaderboardGridCategory.jsx';

import LeaderboardCategory, {
  loader as LeaderboardCategoryLoader,
} from './route/LeaderboardCategory.jsx';

import StoryCategoriesCategory, {
  loader as StoryCategoriesCategoryLoader,
} from './route/StoryCategoriesCategory.jsx';

import ShortsCategoriesCategory, {
  loader as ShortsCategoriesCategoryLoader,
} from './route/ShortsCategoriesCategory.jsx';

import FlixCategoriesCategory, {
  loader as FlixCategoriesCategoryLoader,
} from './route/FlixCategoriesCategory.jsx';

import SearchTypeCategory, {
  loader as SearchTypeCategoryLoader,
} from './route/SearchTypeCategory.jsx';

import SearchType, { loader as SearchTypeLoader } from './route/SearchType.jsx';

import CategoryOverview, {
  loader as CategoryOverviewLoader,
} from './route/CategoryOverview.jsx';

import MessageId, { loader as MessageIdLoader } from './route/MessageId.jsx';

import MessagesMessageId, {
  loader as MessagesMessageIdLoader,
} from './route/MessagesMessageId.jsx';

/**
 * The routes that do not require authentication.
 */
const PUBLIC_ROUTES = [
  {
    index: true,
    Component: Home,
    loader: HomeLoader,
  },
  {
    path: 'video',
    Component: Home,
    loader: HomeLoader,
  },
  {
    path: 'livestream',
    Component: Home,
    loader: HomeLoader,
  },
  {
    path: 'feed',
    Component: Feed,
    loader: FeedLoader,
  },
  {
    path: 'shorts',
    lazy: async () => {
      const { default: Component } = await import('./route/ShortsLayout.jsx');
      return { Component };
    },
    children: [
      {
        index: true,
        Component: Shorts,
        loader: ShortsLoader,
      },
      {
        path: ':messageId',
        Component: ShortsMessageId,
        loader: ShortsMessageIdLoader,
      },
      {
        path: 'ad/:cta',
        Component: ShortsMessageId,
        loader: ShortsMessageIdLoader,
      },
    ],
  },
  {
    path: 'following',
    Component: Home,
    loader: HomeLoader,
  },
  {
    path: 'post/:messageId',
    Component: PostMessageIdLayout,
    loader: PostMessageIdLayoutLoader,
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component } = await import(
            './route/PostMessageId.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'clip/:range',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/PostMessageIdClipRange.jsx'
          );
          return { Component, loader };
        },
      },
    ],
  },
  {
    path: 'user/:userId',
    Component: UserIdLayout,
    loader: UserIdLayoutLoader,
    shouldRevalidate: UserIdLayoutShouldRevalidate,
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component } = await import('./route/UserId.jsx');
          return { Component };
        },
      },
      {
        path: ':listType/:publishStatus',
        lazy: async () => {
          const { default: Component } = await import('./route/UserId.jsx');
          return { Component };
        },
      },
      {
        path: ':listType',
        lazy: async () => {
          const { default: Component } = await import('./route/UserId.jsx');
          return { Component };
        },
      },
    ],
  },
  {
    path: 'u/:username',
    Component: UsernameLayout,
    loader: UsernameLayoutLoader,
    shouldRevalidate: UsernameLayoutShouldRevalidate,
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component } = await import('./route/Username.jsx');
          return { Component };
        },
      },
      {
        path: ':listType/:publishStatus',
        lazy: async () => {
          const { default: Component } = await import('./route/Username.jsx');
          return { Component };
        },
      },
      {
        path: ':listType',
        lazy: async () => {
          const { default: Component } = await import('./route/Username.jsx');
          return { Component };
        },
      },
    ],
  },
  {
    path: `user/:userId/${LIVESTREAM_PATH}/chat`,
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/UserIdLivestreamChat.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: `user/:userId/${LIVESTREAM_PATH}`,
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/UserLivestream.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'story/:messageId',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/StoryMessageId.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'message/:messageId',
    Component: MessageId,
    loader: MessageIdLoader,
  },
  {
    path: 'category-overview',
    Component: CategoryOverview,
    loader: CategoryOverviewLoader,
  },
  {
    path: 'category/:categoryId',
    Component: CategoryIdLayout,
    children: [
      {
        index: true,
        Component: CategoryId,
        loader: CategoryIdLoader,
      },
      {
        path: ':listType',
        Component: CategoryIdListType,
        loader: CategoryIdListTypeLoader,
      },
    ],
  },
  {
    path: 'chat',
    lazy: async () => {
      const { default: Component } = await import('./route/ChatLayout.jsx');
      return { Component };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/Chat.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: 'start',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/ChatStart.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: 'user/:userId',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/ChatUserId.jsx'
          );
          return { Component, loader };
        },
      },
    ],
  },
  {
    path: 'product/:productId',
    Component: ProductId,
    loader: ProductIdLoader,
  },
  {
    path: 'user-feed-grid/:category',
    Component: UserFeedGridCategory,
    loader: UserFeedGridCategoryLoader,
  },
  {
    path: 'user-leaderboard-grid/:category',
    Component: UserLeaderboardGridCategory,
    loader: UserLeaderboardGridCategoryLoader,
  },
  {
    path: 'leaderboard/:region/:category/:range',
    Component: LeaderboardCategory,
    loader: LeaderboardCategoryLoader,
  },
  {
    path: 'user-following-grid/:category',
    Component: UserFollowingGridCategory,
    loader: UserFollowingGridCategoryLoader,
  },
  {
    path: 'user-event',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/UserEvent.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'iframe',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/Iframe.jsx');
      return { Component, loader };
    },
  },
  {
    path: 'livestream',
    lazy: async () => {
      const { default: Component } = await import(
        './route/LivestreamLayout.jsx'
      );
      return { Component };
    },
    children: [
      {
        path: ':index',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/LivestreamIndex.jsx'
          );
          return { Component, loader };
        },
      },
    ],
  },
  {
    path: 'story-categories/:category',
    Component: StoryCategoriesCategory,
    loader: StoryCategoriesCategoryLoader,
  },
  {
    path: 'shorts-categories/:category',
    Component: ShortsCategoriesCategory,
    loader: ShortsCategoriesCategoryLoader,
  },
  {
    path: 'story-preview/:messageId',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/StoryPreviewMessageId.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'flix-categories/:category',
    Component: FlixCategoriesCategory,
    loader: FlixCategoriesCategoryLoader,
  },
  {
    path: 'user-categories/:category',
    Component: UserCategoriesCategory,
    loader: UserCategoriesCategoryLoader,
  },
  {
    path: 'flix/:messageId',
    Component: FlixMessageId,
    loader: FlixMessageIdLoader,
  },
  {
    path: 'messages/:messageId',
    Component: MessagesMessageId,
    loader: MessagesMessageIdLoader,
  },
  {
    path: 'hashtag/:hashtag',
    Component: HashtagHashtag,
    loader: HashtagHashtagLoader,
  },
  {
    path: 'hashtag/:hashtag/:type',
    Component: HashtagHashtagType,
    loader: HashtagHashtagTypeLoader,
  },
  {
    path: 'search',
    lazy: async () => {
      const { default: Component } = await import('./route/SearchLayout.jsx');
      return { Component };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component } = await import('./route/Search.jsx');
          return { Component };
        },
      },
      {
        path: ':type/:category',
        Component: SearchTypeCategory,
        loader: SearchTypeCategoryLoader,
      },
      {
        path: ':type',
        Component: SearchType,
        loader: SearchTypeLoader,
      },
    ],
  },
  {
    path: 'story',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/Story.jsx');
      return { Component, loader };
    },
  },
  {
    path: 'explore',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/Explore.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'referral',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/Referral.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'settings',
    lazy: async () => {
      const { default: Component } = await import('./route/SettingsLayout.jsx');
      return { Component };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/Settings.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: '2257',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/Settings2257.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: 'redeem',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/SettingsRedeem.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: 'bank-account',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/SettingsBankAccount.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: 'notification',
        lazy: async () => {
          const { default: Component, loader } = await import(
            './route/SettingsNotification.jsx'
          );
          return { Component, loader };
        },
      },
      {
        path: 'preferences',
        Component: SettingsPreferences,
        loader: SettingsPreferencesLoader,
      },
      {
        path: 'faq',
        Component: SettingsFaq,
        loader: SettingsFaqLoader,
      },
      {
        path: 'more',
        lazy: async () => {
          const { default: Component } = await import(
            './route/SettingsMoreLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/SettingsMore.jsx'
              );
              return { Component, loader };
            },
          },
          {
            path: 'debug',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/SettingsMoreDebug.jsx'
              );
              return { Component, loader };
            },
          },
          {
            path: 'drm',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/SettingsMoreDrm.jsx'
              );
              return { Component, loader };
            },
          },
          {
            path: 'about',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/SettingsMoreAbout.jsx'
              );
              return { Component, loader };
            },
          },
          {
            path: 'delete-account',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/SettingsMoreDeleteAccount.jsx'
              );
              return { Component, loader };
            },
          },
        ],
      },
    ],
  },
  {
    path: 'tos',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/Tos.jsx');
      return { Component, loader };
    },
  },
  {
    path: 'privacy',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/Privacy.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'join',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/Join.jsx');
      return { Component, loader };
    },
  },
  {
    path: 'join/:progress',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/JoinProgress.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'age-verify/:progress',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/AgeVerifyProgress.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'standalone',
    lazy: async () => {
      const { default: Component } = await import(
        './route/StandaloneLayout.jsx'
      );
      return { Component };
    },
    children: [
      {
        path: 'contractor',
        lazy: async () => {
          const { default: Component } = await import(
            './route/StandaloneContractorLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            path: 'example',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/StandaloneContractorExample.jsx'
              );
              return { Component, loader };
            },
          },
        ],
      },
      {
        path: 'game',
        lazy: async () => {
          const { default: Component } = await import(
            './route/StandaloneGameLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            path: 'sic-bo',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/StandaloneGameSicBo.jsx'
              );
              return { Component, loader };
            },
          },
        ],
      },
      {
        path: 'player',
        lazy: async () => {
          const { default: Component } = await import(
            './route/StandalonePlayerLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            path: 'byteplus-rtc',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/StandalonePlayerByteplusRtc.jsx'
              );
              return { Component, loader };
            },
          },
          {
            path: 'byteplus/:streamId',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/StandalonePlayerByteplusStreamId.jsx'
              );
              return { Component, loader };
            },
          },
          {
            path: 'byteplus',
            lazy: async () => {
              const { default: Component, loader } = await import(
                './route/StandalonePlayerByteplus.jsx'
              );
              return { Component, loader };
            },
          },
        ],
      },
    ],
  },
  {
    path: 'acceptable-use-policy',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/AcceptableUsePolicy.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'anti-slavery',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/AntiSlavery.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'complaint-policy',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/ComplaintPolicy.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'cookie-policy',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/CookiePolicy.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: '404',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/404.jsx');
      return { Component, loader };
    },
  },
  {
    path: 'geo-blocked',
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/GeoBlocked.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'error',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/Error.jsx');
      return { Component, loader };
    },
  },
  {
    path: '*',
    lazy: async () => {
      const { default: Component, loader } = await import('./route/404.jsx');
      return { Component, loader };
    },
  },
];

/**
 * The routes that require authentication.
 */
const REQUIRE_AUTH_ROUTES = [
  {
    path: `:messageId/${ProfileListPublishStatus.DRAFT}`,
    lazy: async () => {
      const { default: Component, loader } = await import(
        './route/ShortsMessageIdDraft.jsx'
      );
      return { Component, loader };
    },
  },
  {
    path: 'chat/:chatroomId/message/:messageId',
    lazy: async () => {
      const { default: Component } = await import('./route/ChatMessageId.jsx');
      return { Component };
    },
  },
  {
    path: 'chat',
    lazy: async () => {
      const { default: Component } = await import('./route/ChatLayout.jsx');
      return { Component };
    },
    children: [
      {
        path: ':chatroomId/media',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ChatIdMedia.jsx'
          );
          return { Component };
        },
      },
      {
        path: ':chatroomId/search',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ChatIdSearch.jsx'
          );
          return { Component };
        },
      },
      {
        path: `${AUTO_CHAT_CHAT_ID}`,
        lazy: async () => {
          const { default: Component } = await import(
            './route/ChatAutoChat.jsx'
          );
          return { Component };
        },
      },
      {
        path: ':chatroomId',
        lazy: async () => {
          const { default: Component } = await import('./route/ChatId.jsx');
          return { Component };
        },
      },
    ],
  },
  {
    path: 'settings',
    lazy: async () => {
      const { default: Component } = await import('./route/SettingsLayout.jsx');
      return { Component };
    },
    children: [
      {
        path: 'vvip',
        lazy: async () => {
          const { default: Component } = await import(
            './route/SettingsVvip.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'referral',
        lazy: async () => {
          const { default: Component } = await import(
            './route/SettingsReferral.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'auto-addvalue',
        lazy: async () => {
          const { default: Component } = await import(
            './route/SettingsAutoAddvalue.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'journal',
        lazy: async () => {
          const { default: Component } = await import(
            './route/SettingsJournal.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'referral/result',
        lazy: async () => {
          const { default: Component } = await import(
            './route/SettingsReferralResult.jsx'
          );
          return { Component };
        },
      },
    ],
  },
  {
    path: 'audio/:messageId',
    lazy: async () => {
      const { default: Component } = await import('./route/AudioMessageId.jsx');
      return { Component };
    },
  },
  {
    path: `me/${ProfileListType.LIVESTREAM_SESSION}/:sessionId`,
    lazy: async () => {
      const { default: Component } = await import(
        './route/MeLivestreamSessionSessionId.jsx'
      );
      return { Component };
    },
  },
  {
    path: `me/${ProfileListType.LIVESTREAM_SESSION}/:sessionId/leaderboard`,
    lazy: async () => {
      const { default: Component } = await import(
        './route/MeLivestreamSessionSessionIdLeaderboard.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'revenue',
    lazy: async () => {
      const { default: Component } = await import('./route/RevenueLayout.jsx');
      return { Component };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { default: Component } = await import('./route/Revenue.jsx');
          return { Component };
        },
      },
      {
        path: 'report/daily',
        lazy: async () => {
          const { default: Component } = await import(
            './route/RevenueReportDaily.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'report/fanleaderboard',
        lazy: async () => {
          const { default: Component } = await import(
            './route/RevenueReportFanleaderboard.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'report/billing',
        lazy: async () => {
          const { default: Component } = await import(
            './route/RevenueReportBilling.jsx'
          );
          return { Component };
        },
      },
    ],
  },
  {
    path: 'archive',
    lazy: async () => {
      const { default: Component } = await import('./route/Archive.jsx');
      return { Component };
    },
  },
  {
    path: 'archive/userId/:id/:listId',
    lazy: async () => {
      const { default: Component } = await import(
        './route/ArchiveUserIdIdListId.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'messagePack/list',
    lazy: async () => {
      const { default: Component } = await import(
        './route/MessagePackList.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'profile/following',
    lazy: async () => {
      const { default: Component } = await import(
        './route/ProfileFollowing.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'profile/follower',
    lazy: async () => {
      const { default: Component } = await import(
        './route/ProfileFollower.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'profile/subscriber',
    lazy: async () => {
      const { default: Component } = await import(
        './route/ProfileSubscriber.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'profile/subscribed',
    lazy: async () => {
      const { default: Component } = await import(
        './route/ProfileSubscribed.jsx'
      );
      return { Component };
    },
  },
  {
    path: 'manage-profile',
    lazy: async () => {
      const { default: Component } = await import(
        './route/ManageProfileLayout.jsx'
      );
      return { Component };
    },
    children: [
      {
        path: `${ManageProfileType.MANAGE}`,
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileManage.jsx'
          );
          return { Component };
        },
      },
      {
        path: `${ManageProfileType.ACCOUNT}`,
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileAccount.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'biography',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileBiography.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'display-name',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileDisplayName.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'account-linking',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileAccountLinkingLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { default: Component } = await import(
                './route/ManageProfileAccountLinking.jsx'
              );
              return { Component };
            },
          },
          {
            path: 'web-authn',
            lazy: async () => {
              const { default: Component } = await import(
                './route/ManageProfileAccountLinkingWebAuthn.jsx'
              );
              return { Component };
            },
          },
        ],
      },
      {
        path: 'blocked-list',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileBlockedList.jsx'
          );
          return { Component };
        },
      },
      {
        path: 'credit-card',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileCreditCardLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { default: Component } = await import(
                './route/ManageProfileCreditCard.jsx'
              );
              return { Component };
            },
          },
          {
            path: 'billing-email',
            lazy: async () => {
              const { default: Component } = await import(
                './route/ManageProfileCreditCardBillingEmail.jsx'
              );
              return { Component };
            },
          },
        ],
      },
      {
        path: 'receipt',
        lazy: async () => {
          const { default: Component } = await import(
            './route/ManageProfileReceiptLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { default: Component } = await import(
                './route/ManageProfileReceipt.jsx'
              );
              return { Component };
            },
          },
          {
            path: 'information',
            lazy: async () => {
              const { default: Component } = await import(
                './route/ManageProfileReceiptInformation.jsx'
              );
              return { Component };
            },
          },
        ],
      },
    ],
  },
  {
    path: 'addvalue',
    lazy: async () => {
      const { default: Component } = await import('./route/Addvalue.jsx');
      return { Component };
    },
  },
];

/**
 * The routes that require none authentication.
 */
const REQUIRE_NONE_AUTH_ROUTES = [
  {
    path: 'login',
    lazy: async () => {
      const { default: Component } = await import('./route/Login.jsx');
      return { Component };
    },
  },
  {
    path: 'signup',
    lazy: async () => {
      const { default: Component } = await import('./route/Signup.jsx');
      return { Component };
    },
  },
];

const routes = [
  {
    path: '/',
    Component: App,
    children: [
      {
        lazy: async () => {
          const { default: Component } = await import(
            './route/NavHeaderLayout.jsx'
          );
          return { Component };
        },
        children: [
          {
            lazy: async () => {
              const { default: Component } = await import(
                './route/PublicLayout.jsx'
              );
              return { Component };
            },
            children: PUBLIC_ROUTES,
          },
          {
            lazy: async () => {
              const { default: Component } = await import(
                './route/RequireAuthLayout.jsx'
              );
              return { Component };
            },
            children: REQUIRE_AUTH_ROUTES,
          },
          {
            lazy: async () => {
              const { default: Component } = await import(
                './route/RequireNoneAuthLayout.jsx'
              );
              return { Component };
            },
            children: REQUIRE_NONE_AUTH_ROUTES,
          },
        ],
      },
    ],
  },
];

export default routes;
